import type { ReactNode } from "react";
import { Bookmark, Heart, MessageCircle, MoreHorizontal, Send, X } from "lucide-react";

import { cn } from "../lib/cn";

export type SocialPostPreviewPlatform = "instagram" | "tiktok" | (string & {});
export type SocialPostPreviewPostType =
  | "feed_portrait"
  | "feed_square"
  | "story"
  | "tiktok_portrait"
  | "tiktok_carousel_portrait"
  | (string & {});
export type SocialPostPreviewVariant = "instagram_feed" | "instagram_story";

export type SocialPostPreviewLabels = Partial<{
  postMenu: string;
  like: string;
  comment: string;
  share: string;
  save: string;
  closeStory: string;
  storyMore: string;
  likeStory: string;
  shareStory: string;
  sendMessage: string;
  storyMetadata: string;
  captionPending: string;
}>;

export type SocialPostPreviewProps = {
  platform: SocialPostPreviewPlatform;
  postType: SocialPostPreviewPostType;
  name: string;
  media: ReactNode;
  mediaOverlay?: ReactNode;
  caption?: string;
  hashtags?: string[];
  className?: string;
  headerAction?: ReactNode;
  showMetadata?: boolean;
  labels?: SocialPostPreviewLabels;
};

const defaultLabels = {
  postMenu: "Social post menu preview",
  like: "Like preview",
  comment: "Comment preview",
  share: "Share preview",
  save: "Save preview",
  closeStory: "Close story preview",
  storyMore: "Story more preview",
  likeStory: "Like story preview",
  shareStory: "Share story preview",
  sendMessage: "Send message",
  storyMetadata: "Story metadata",
  captionPending: "Caption pending",
} satisfies Required<SocialPostPreviewLabels>;

export const resolveSocialPostPreviewVariant = (
  platform: SocialPostPreviewPlatform,
  postType: SocialPostPreviewPostType,
): SocialPostPreviewVariant => {
  if ((platform === "instagram" && postType === "story") || platform === "tiktok") {
    return "instagram_story";
  }

  return "instagram_feed";
};

export const formatSocialPostHashtagToken = (value: string): string => {
  const token = value.trim().replace(/^#+/, "").replace(/\s+/g, "");
  return token ? `#${token}` : "";
};

export function SocialPostPreview({
  platform,
  postType,
  name,
  media,
  mediaOverlay,
  caption = "",
  hashtags = [],
  className,
  headerAction,
  showMetadata = true,
  labels,
}: SocialPostPreviewProps) {
  const mergedLabels = { ...defaultLabels, ...labels };
  const variant = resolveSocialPostPreviewVariant(platform, postType);

  if (variant === "instagram_story") {
    return (
      <InstagramStoryPreview
        name={name}
        mediaOverlay={mediaOverlay}
        caption={caption}
        hashtags={hashtags}
        className={className}
        headerAction={headerAction}
        showMetadata={showMetadata}
        labels={mergedLabels}
      >
        {media}
      </InstagramStoryPreview>
    );
  }

  return (
    <InstagramFeedPreview
      name={name}
      mediaOverlay={mediaOverlay}
      caption={caption}
      hashtags={hashtags}
      className={className}
      headerAction={headerAction}
      showMetadata={showMetadata}
      labels={mergedLabels}
    >
      {media}
    </InstagramFeedPreview>
  );
}

function InstagramFeedPreview({
  name,
  mediaOverlay,
  caption,
  hashtags,
  className,
  headerAction,
  showMetadata,
  labels,
  children,
}: {
  name: string;
  mediaOverlay?: ReactNode;
  caption: string;
  hashtags: string[];
  className?: string;
  headerAction?: ReactNode;
  showMetadata: boolean;
  labels: Required<SocialPostPreviewLabels>;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-[2.45rem] border border-[#ddd6cc] bg-[#fffefa] text-[#0f0f10] shadow-[0_30px_90px_rgba(15,15,16,0.2),0_2px_0_rgba(255,255,255,0.8)_inset]",
        className,
      )}
    >
      <div className="flex h-[84px] items-center justify-between border-b border-black/[0.055] bg-[linear-gradient(180deg,#fffefa,#fff8f1)] px-12">
        <SocialProfileLabel label={name} />
        {headerAction ?? <ChromeButton label={labels.postMenu} icon={<MoreHorizontal />} />}
      </div>
      <div className="bg-white px-12">
        <div className="relative">
          {children}
          {mediaOverlay ? <div className="absolute inset-0 z-10">{mediaOverlay}</div> : null}
        </div>
      </div>
      <div className="flex h-16 items-center justify-between border-b border-black/[0.05] bg-[#fffefa] px-12">
        <div className="flex items-center gap-4 text-[#1b1b1d]">
          <ChromeButton label={labels.like} icon={<Heart />} />
          <ChromeButton label={labels.comment} icon={<MessageCircle />} />
          <ChromeButton label={labels.share} icon={<Send />} />
        </div>
        <ChromeButton label={labels.save} icon={<Bookmark />} />
      </div>
      <div className="max-h-80 overflow-y-auto bg-[#fffefa] px-12 pb-9 pt-5 text-[30px] leading-[1.34]">
        {showMetadata ? (
          <div className="space-y-3">
            <div>
              <span className="font-semibold tracking-[-0.01em]">{name}</span>{" "}
              <span className="whitespace-pre-wrap text-[#1f1f22]">
                {caption.trim() || labels.captionPending}
              </span>
            </div>
            <HashtagPreview tokens={hashtags} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function InstagramStoryPreview({
  name,
  mediaOverlay,
  caption,
  hashtags,
  className,
  headerAction,
  showMetadata,
  labels,
  children,
}: {
  name: string;
  mediaOverlay?: ReactNode;
  caption: string;
  hashtags: string[];
  className?: string;
  headerAction?: ReactNode;
  showMetadata: boolean;
  labels: Required<SocialPostPreviewLabels>;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex items-start gap-10", className)}>
      <div className="relative overflow-hidden rounded-[4.5rem] border-[14px] border-[#111113] bg-[#111113] shadow-[0_34px_100px_rgba(15,15,16,0.34)]">
        <div className="absolute left-8 right-8 top-8 z-10 flex gap-2">
          <span className="h-1.5 flex-1 rounded-full bg-white/95" aria-hidden="true" />
          <span className="h-1.5 flex-1 rounded-full bg-white/45" aria-hidden="true" />
        </div>
        <div className="absolute left-8 right-8 top-16 z-10 flex items-center justify-between text-white drop-shadow">
          <SocialProfileLabel label={name} inverted={true} />
          <div className="flex items-center gap-4">
            {headerAction ?? <ChromeButton label={labels.storyMore} icon={<MoreHorizontal />} />}
            <ChromeButton label={labels.closeStory} icon={<X />} />
          </div>
        </div>
        <div className="relative">
          {children}
          {mediaOverlay ? <div className="absolute inset-0 z-10">{mediaOverlay}</div> : null}
        </div>
        <div className="absolute bottom-7 left-7 right-7 z-10 flex items-center gap-4 text-white">
          <div className="flex h-16 flex-1 items-center rounded-full border border-white/65 bg-black/18 px-6 text-[22px] font-medium backdrop-blur-sm">
            {labels.sendMessage}
          </div>
          <ChromeButton label={labels.likeStory} icon={<Heart />} />
          <ChromeButton label={labels.shareStory} icon={<Send />} />
        </div>
      </div>
      {showMetadata ? (
        <div className="w-[320px] rounded-[2rem] border border-black/[0.08] bg-[#fffefa] p-7 text-[#0f0f10] shadow-[0_24px_70px_rgba(15,15,16,0.16)]">
          <div className="mb-5 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#737373]">
            {labels.storyMetadata}
          </div>
          <div className="max-h-[18rem] space-y-4 overflow-hidden text-[22px] leading-[1.35]">
            <div className="line-clamp-6">
              <span className="font-semibold">{name}</span>{" "}
              <span className="whitespace-pre-wrap text-[#1f1f22]">
                {caption.trim() || labels.captionPending}
              </span>
            </div>
            <HashtagPreview tokens={hashtags} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SocialProfileLabel({ label, inverted = false }: { label: string; inverted?: boolean }) {
  const initial = label.trim().charAt(0).toUpperCase() || "B";

  return (
    <div className="flex min-w-0 items-center gap-4">
      <span
        className={cn(
          "flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[conic-gradient(from_210deg,#feda75,#fa7e1e,#d62976,#962fbf,#4f5bd5,#feda75)] p-[2px]",
          inverted && "h-12 w-12",
        )}
        aria-hidden="true"
      >
        <span
          className={cn(
            "flex h-full w-full items-center justify-center rounded-full bg-white text-[22px] font-bold text-[#0f0f10] shadow-[0_0_0_1px_rgba(15,15,16,0.06)_inset]",
            inverted && "bg-[#1b1b1d] text-white",
          )}
        >
          {initial}
        </span>
      </span>
      <span className="min-w-0 truncate text-[31px] font-semibold leading-none tracking-[-0.02em]">
        {label}
      </span>
    </div>
  );
}

function ChromeButton({ label, icon }: { label: string; icon: ReactNode }) {
  return (
    <button
      type="button"
      className="flex h-[3.25rem] w-[3.25rem] items-center justify-center rounded-full text-current disabled:opacity-100 [&_svg]:h-8 [&_svg]:w-8 [&_svg]:stroke-[1.8]"
      aria-label={label}
      disabled={true}
    >
      {icon}
    </button>
  );
}

function HashtagPreview({ tokens }: { tokens: string[] }) {
  if (tokens.length === 0) {
    return null;
  }

  return (
    <div className="flex min-w-0 flex-wrap gap-x-2 gap-y-1.5 text-[0.78em] leading-none text-[#0b54d6]">
      {tokens.map((token, index) => (
        <span key={`${token}-${index}`} className="max-w-full truncate font-medium">
          {formatSocialPostHashtagToken(token)}
        </span>
      ))}
    </div>
  );
}
